import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { readConfig } from "../lib/config";
import { useAdminAuth } from "../hooks/use-admin-auth";
import { ConfigError } from "@pgs-core/ui/components/config-error";
import { LoginScreen } from "@pgs-core/ui/components/login-screen";
import { SetPasswordScreen } from "@pgs-core/ui/components/set-password-screen";
import { ForbiddenScreen } from "@pgs-core/ui/components/forbidden-screen";
import { LoadingScreen } from "@pgs-core/ui/components/loading-screen";
import { AdminShell } from "./admin-shell";
import { LoginRevealTransition } from "./login-reveal";
import { DashboardPage } from "../pages/dashboard-page.jsx";
import { ClientsPage } from "../pages/clients-page.jsx";
import { ClientNewPage } from "../pages/client-new-page.jsx";
import { ClientLayout } from "../pages/client-layout.jsx";
import { ClientOverviewPage } from "../pages/client-overview-page.jsx";
import { ClientUsersPage } from "../pages/client-users-page.jsx";
import { ClientBrandingPage } from "../pages/client-branding-page.jsx";
import { ClientBillingPage } from "../pages/client-billing-page.jsx";
import { ClientModulesPage } from "../pages/client-modules-page.jsx";
import { ClientUsagePage } from "../pages/client-usage-page.jsx";
import { ClientSitesPage } from "../pages/client-sites-page.jsx";
import { ClientSiteDetailPage } from "../pages/client-site-detail-page";
import { BillingPage } from "../pages/billing-page.jsx";
import { ActivityPage } from "../pages/activity-page.jsx";
import { SupportPage } from "../pages/support-page";
import { InfrastructureLayout } from "../pages/infrastructure-layout.jsx";
import { InfrastructurePage } from "../pages/infrastructure-page.jsx";
import { InfrastructureDatabaseBackupPage } from "../pages/infrastructure-database-backup-page.jsx";
import { InfrastructureSecurityPage } from "../pages/infrastructure-security-page.jsx";
import { InfrastructureGdprPage } from "../pages/infrastructure-gdpr-page.jsx";
import { SettingsPage } from "../pages/settings-page.jsx";
import { AccountPage } from "../pages/account-page";

const config = readConfig();

export function App() {
  const auth = useAdminAuth(config);
  const { view, error } = auth;

  if (view === "config-error") return <ConfigError message={error} />;
  if (view === "loading") return <LoadingScreen message="Checking admin session" />;
  if (view === "login") {
    return <LoginScreen title="Admin Portal" onSignIn={auth.signIn} error={error} signedIn={false} />;
  }
  if (view === "set-password") {
    return <SetPasswordScreen email={auth.recoveryEmail} onSetPassword={auth.completeSetPassword} error={error} />;
  }
  if (view === "forbidden") {
    return <ForbiddenScreen title="Admin claim required" message={error} onRetry={auth.signOut} onSignOut={auth.signOut} />;
  }

  return (
    <LoginRevealTransition
      play={auth.enteredFromLogin}
      renderLoginScreen={(onTransitionDone) => (
        <LoginScreen title="Admin Portal" onSignIn={auth.signIn} error={error} signedIn={true} onTransitionDone={onTransitionDone} />
      )}
    >
      <BrowserRouter>
        <Routes>
          <Route
            element={
              <AdminShell
                user={auth.user}
                api={auth.api}
                environment={auth.environment}
                onSignOut={auth.signOut}
                changePassword={auth.changePassword}
              />
            }
          >
            {/* Temporary: land on Clients by default; Dashboard stays reachable. */}
            <Route index element={<Navigate to="/clients" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/new" element={<ClientNewPage />} />
            <Route path="clients/:clientId" element={<ClientLayout />}>
              <Route index element={<ClientOverviewPage />} />
              <Route path="users" element={<ClientUsersPage />} />
              <Route path="branding" element={<ClientBrandingPage />} />
              {/* Legacy bookmarks: Design was renamed to Branding. */}
              <Route path="design" element={<Navigate to="../branding" replace />} />
              <Route path="modules" element={<ClientModulesPage />} />
              <Route path="usage" element={<ClientUsagePage />} />
              <Route path="billing" element={<ClientBillingPage />} />
              <Route path="sites" element={<ClientSitesPage />} />
              <Route path="sites/:siteId" element={<ClientSiteDetailPage />} />
            </Route>
            <Route path="billing" element={<BillingPage />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="infrastructure" element={<InfrastructureLayout />}>
              <Route index element={<InfrastructurePage />} />
              <Route path="database-backup" element={<InfrastructureDatabaseBackupPage />} />
              <Route path="security" element={<InfrastructureSecurityPage />} />
              <Route path="gdpr" element={<InfrastructureGdprPage />} />
            </Route>
            <Route path="settings" element={<SettingsPage />} />
            <Route path="account" element={<AccountPage />} />
            {/* Catch-all: redirect unknown paths to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </LoginRevealTransition>
  );
}
